import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { useMemo } from 'react'

import { listSendableMailboxes } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListSendableMailboxesRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'

/** A sender identity the compose or reply sheet can offer in its "From" select. */
export interface EmailSenderOption {
  id: string
  emailAddress: string
  displayName: string
  isDefault: boolean
  userId: string
  /**
   * The caller may send as this mailbox: it is theirs, the org default, or
   * shared with them. Others are offered disabled.
   */
  canSend: boolean
}

interface UseEmailSenderOptionsParams {
  organizationId: string
  open: boolean
  userId?: string
}

const NO_SENDER_OPTIONS: readonly EmailSenderOption[] = []

/**
 * Connected mailboxes for the compose and reply "From" select. The server orders them
 * (the caller's own first, then the org default, then ones shared with them,
 * then the rest), marks which the caller may send as, and names the one to
 * preselect, which is also the mailbox it sends from when nothing is picked.
 *
 * The list needs the EmailInbox add-on, so an org without it makes no RPC.
 */
export function useEmailSenderOptions(params: UseEmailSenderOptionsParams) {
  const { organizationId, open } = params
  const { hasFeature: hasEmailInboxFeature } = useHasEmailInboxFeature()
  const enabled = open && hasEmailInboxFeature && !!organizationId

  const request = useMemo(
    () => create(ListSendableMailboxesRequestSchema, { organizationId }),
    [organizationId],
  )
  const query = useQuery(listSendableMailboxes, request, { enabled })

  const senderOptions = useMemo<readonly EmailSenderOption[]>(() => {
    const mailboxes = query.data?.mailboxes
    if (!enabled || !mailboxes?.length) return NO_SENDER_OPTIONS
    return mailboxes.map((mailbox) => ({
      id: mailbox.id,
      emailAddress: mailbox.emailAddress,
      displayName: mailbox.displayName || mailbox.userName,
      isDefault: mailbox.isOrgDefault,
      userId: mailbox.userId,
      canSend: mailbox.callerCanSend,
    }))
  }, [enabled, query.data])

  return {
    senderOptions,
    defaultAccountId: enabled ? (query.data?.defaultMailboxId ?? '') : '',
    hasEmailInboxFeature,
    isLoading: enabled && query.isLoading,
    // A failed list is not "no mailbox": the sheet must not send the user to
    // Settings to connect an inbox they already have.
    isError: enabled && query.isError,
  }
}
